/*
  Heat Wishlist: storefront runtime. Replaces the Wishlist Hero app.

  Loaded with `defer` by snippets/custom-wishlist.liquid, which also emits the
  #heat-wishlist-config JSON block this file reads. No build step, no deps.

  Two storage modes, one state shape:
    - logged in : the canonical customer metafield heat.wishlist_items
                  ({ v:2, items:[{vid, pid, handle, at, list}] }, oldest first,
                  UNCAPPED), mutated through the /apps/wishlist app proxy.
                  Initial state is server-rendered into the config block, so
                  nothing is fetched on page load.
    - guest     : localStorage key `heat_wishlist`
                  { v:1, items:[{vid, pid, handle, at, list}] }.

  Both modes render the SAME light cards on /pages/wishlist, from this state.
  There is no server-rendered card grid any more.

  There is no item cap on either side.

  `list` is a free-text label (up to 40 characters, "" = no list). Shoppers set
  it per card on /pages/wishlist. Signed in it is written through
  /apps/wishlist/label; a guest's label lives in localStorage like the rest of
  the row. Labels drive the filter chips above the grid; the sort select next
  to them is pure client-side ordering of the SAME rows. Both choices are
  remembered per browser (localStorage, best effort).

  Lists stay invisible to anyone not using them. With no label anywhere there
  is no per-card List select at all, only the toolbar's Create a list button;
  making a label there is what reveals the selects, and the button then reads
  New list.

  SHARED MODE. /pages/wishlist?share=<token> is the gift-giver view of somebody
  else's whole wishlist; /pages/wishlist?share=<token>&list=<label> is ONE of
  their lists, and that token is bound to the label, so the link is invalid
  without its &list= (the recipient never sees the rest). The owner's panel
  only ever shows URLs the server built (/share and /share?list=); nothing
  here assembles a list link. THE SERVER DOES THE FILTERING
  (GET /apps/wishlist/shared?t=<token>&list=<label> answers
  { ok, owner, list, items }); nothing here filters the rows again. An older
  whole-wishlist link may still carry &list= as a plain filter, and the server
  honours that too. It is a different page from the same section: no save
  state, no merge, no list controls and no Remove, so nothing here can write to
  the visitor's own wishlist. Marking a bottle bought is
  POST /apps/wishlist/shared/purchased, with no name asked for and none
  stored: the chip just reads "Already bought". Both endpoints work for
  signed-in visitors and guests alike. The header hearts keep showing the
  VISITOR's own saved count, untouched.

  Guests must never call the proxy: Shopify forwards no customer id and the
  Worker answers 401 {ok:false,error:'login'}.

  Public API (window.HeatWishlist): has(vid), add({vid,pid,handle}),
  remove(vid), items(). Every change dispatches `heat:wishlist:changed` on
  document with detail { items }.

  Cart adds go through window.HeatCart (snippets/custom-cart-api.liquid) and
  are always batched into a single request: see the cart mutation race note
  in docs/CART-INTEGRATION.md.
*/
(function () {
  'use strict';

  if (window.HeatWishlist) return;

  var GUEST_KEY = 'heat_wishlist';
  var LEGACY_KEY = 'wishlisthero_.wl-itms';
  /*
    Signed-in only. The guest import below repeats on every load, which is what
    lets a guest recover their old list at any time. An account cannot work
    that way: its list is the metafield, not `heat_wishlist`, so a repeated
    import would keep pushing rows the shopper has deliberately removed from
    their account back into it. This flag is set once the legacy rows have
    landed in the account, and only gates the signed-in path.
  */
  var LEGACY_MERGED_KEY = 'heat_wishlist_wlh_merged';

  /* Per-browser view preferences. All best effort. */
  var FILTER_KEY = 'heat_wishlist_filter';
  var SORT_KEY = 'heat_wishlist_sort';

  var MAX_LABEL = 40;

  /*
    The select itself is rendered by sections/heat-wishlist.liquid. This list
    only validates what comes back out of localStorage, so a stale or hand
    edited value falls back to the default instead of sorting by nothing.
  */
  var SORT_VALUES = ['date', 'price-asc', 'price-desc', 'brand', 'heat', 'sale', 'stock'];

  var config = readConfig();
  var customerId = config.customerId || null;
  var proxyBase = config.proxyBase || '/apps/wishlist';
  var pageUrl = config.pageUrl || '/pages/wishlist';
  var state = normalizeItems(config.items || []);
  var busy = false;

  /*
    Shared mode is decided once, from the URL, before anything renders. Every
    owner-side code path checks it: a gift-giver must never write to the list
    they are looking at, and must never have their OWN list touched either.
  */
  var shareToken = readShareToken();
  var sharedMode = !!shareToken;
  /*
    Optional sublist. It is sent with every shared request exactly as it came
    in: a list link's token only verifies together with its own label, and on
    an older whole-wishlist link it is a filter. Either way the FILTERING IS
    THE SERVER'S; nothing here filters the returned rows a second time.
  */
  var sharedList = sharedMode ? readQueryParam('list') : '';

  var viewFilter = readPref(FILTER_KEY, '');
  var viewSort = readPref(SORT_KEY, 'date');
  if (SORT_VALUES.indexOf(viewSort) === -1) viewSort = 'date';

  /* Set by a label change so the rebuilt grid can put focus back. */
  var focusListVid = '';

  /*
    Labels the shopper made this session with the Create a list button but has
    not put anything in yet. They are not in `state`, so labelsInUse() cannot
    see them, and they are deliberately not persisted: an empty list that is
    never used is gone on the next load and nobody has to tidy it up.
  */
  var newLabels = [];

  function readShareToken() {
    return readQueryParam('share');
  }

  function readQueryParam(name) {
    var match = new RegExp('[?&]' + name + '=([^&#]*)').exec(window.location.search || '');
    if (!match) return '';
    try {
      return decodeURIComponent(match[1].replace(/\+/g, ' '));
    } catch (e) {
      return match[1];
    }
  }

  function readPref(key, fallback) {
    try {
      var raw = window.localStorage.getItem(key);
      return raw == null ? fallback : String(raw);
    } catch (e) {
      return fallback;
    }
  }

  function writePref(key, value) {
    try {
      window.localStorage.setItem(key, String(value));
    } catch (e) { /* private mode, quota, blocked storage */ }
  }

  /* ----------------------------------------------------------- config */

  function readConfig() {
    var el = document.getElementById('heat-wishlist-config');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent || el.innerHTML || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  /* ------------------------------------------------------------ state */

  function normalizeItems(list) {
    if (!Array.isArray(list)) return [];
    var seen = {};
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var raw = list[i];
      if (!raw) continue;
      var vid = String(raw.vid == null ? '' : raw.vid);
      if (!vid || seen[vid]) continue;
      seen[vid] = true;
      out.push({
        vid: vid,
        pid: raw.pid == null ? '' : String(raw.pid),
        handle: raw.handle == null ? '' : String(raw.handle),
        at: raw.at == null ? '' : String(raw.at),
        list: cleanLabel(raw.list)
      });
    }
    return out;
  }

  /*
    One label rule for every entry point (the select, the New list prompt, the
    server payload, a guest row read back off localStorage): drop control
    characters and < >, collapse runs of whitespace, trim, cap at 40
    characters. "" means no list.

    The first step mirrors the Worker's cleanList (broker/wishlist.js strips
    [\u0000-\u001f<>]). Without it a signed-in label like "<Mom>" went up as
    typed and came back as "Mom", so the label changed under the shopper after
    the round trip. Stripped first, a tab or newline disappears exactly as it
    does on the server instead of turning into a space.

    The cap counts UTF-16 units, so an emoji at characters 40-41 is cut in half;
    the lone high surrogate left behind makes encodeURIComponent throw and the
    share link fail, so it is dropped. Every step here must match cleanList
    step for step: a list link's token covers the cleaned label, so changing
    either side later breaks links already sent.
  */
  function cleanLabel(value) {
    if (value == null) return '';
    return String(value)
      .replace(/[\u0000-\u001f<>]/g, '')
      .replace(/\s+/g, ' ')
      .replace(/^ | $/g, '')
      .slice(0, MAX_LABEL)
      .replace(/[\uD800-\uDBFF]$/, '')
      .replace(/ $/, '');
  }

  /* Every label in use, unique, A to Z, case-insensitive. */
  function labelsInUse() {
    return sortedLabels(state.map(function (row) { return row.list; }));
  }

  /*
    Every label the List selects offer: the ones in use plus the ones made this
    session. The filter chips deliberately do NOT use this, because a chip for
    a list with nothing in it would only ever show an empty grid.
  */
  function labelsOffered() {
    return sortedLabels(state.map(function (row) { return row.list; }).concat(newLabels));
  }

  /*
    The List selects only exist once there is a list to put something in. With
    no labels at all a shopper never sees the feature: the toolbar's Create a
    list button is the single way in.
  */
  function listsInUse() {
    return labelsOffered().length > 0;
  }

  function sortedLabels(values) {
    var seen = {};
    var out = [];
    for (var i = 0; i < values.length; i++) {
      var label = values[i];
      if (!label) continue;
      var key = label.toLowerCase();
      if (seen[key]) continue;
      seen[key] = true;
      out.push(label);
    }
    out.sort(function (a, b) {
      return a.toLowerCase() < b.toLowerCase() ? -1 : (a.toLowerCase() > b.toLowerCase() ? 1 : 0);
    });
    return out;
  }

  function setState(list) {
    state = normalizeItems(list);
    if (!customerId) writeGuest(state);
    render();
    try {
      document.dispatchEvent(new CustomEvent('heat:wishlist:changed', {
        bubbles: true,
        detail: { items: state.slice() }
      }));
    } catch (e) { /* never let the hook break a wishlist action */ }
  }

  function indexOfVid(vid) {
    for (var i = 0; i < state.length; i++) {
      if (state[i].vid === vid) return i;
    }
    return -1;
  }

  /* -------------------------------------------------- guest storage */

  function readGuest() {
    try {
      var raw = window.localStorage.getItem(GUEST_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.items)) return [];
      return normalizeItems(parsed.items);
    } catch (e) {
      return [];
    }
  }

  function writeGuest(list) {
    try {
      window.localStorage.setItem(GUEST_KEY, JSON.stringify({ v: 1, items: list }));
    } catch (e) { /* private mode, quota, blocked storage */ }
  }

  function clearGuest() {
    try {
      window.localStorage.removeItem(GUEST_KEY);
    } catch (e) { /* ignore */ }
  }

  function legacyMergedIntoAccount() {
    try {
      return !!window.localStorage.getItem(LEGACY_MERGED_KEY);
    } catch (e) {
      return false;
    }
  }

  function markLegacyMergedIntoAccount() {
    try {
      window.localStorage.setItem(LEGACY_MERGED_KEY, '1');
    } catch (e) { /* private mode, quota, blocked storage */ }
  }

  /*
    Import of Wishlist Hero's own localStorage (store2 namespace
    'wishlisthero_' with the default '.' delimiter, so the key is
    `wishlisthero_.wl-itms`; value is a JSON array).

    This runs on EVERY load, not once: there is no first-run flag and no
    expiry, so a shopper can recover their old list for as long as the legacy
    key survives in their browser. It is idempotent by variant id: a legacy
    row whose vid is already in `heat_wishlist` is skipped: and the old key is
    only ever read, never written and never deleted. The trade-off is
    deliberate: removing a card that is still in the legacy list brings it back
    on the next load, which is what "recover my old list" means here.

    Its writer upper-cases the first letter of every field and keeps the
    original too, so an entry may carry ProductVariantId or productVariantId,
    ProductId or productId, ProductLink or productLink, and AddedOn as a
    `Date.toString()` string. Nothing is guaranteed: a variant id alone is
    enough to keep, and the handle is dug out of the product link when there
    is one.
  */
  function importLegacy(existing) {
    var legacy = [];
    try {
      var raw = window.localStorage.getItem(LEGACY_KEY);
      if (raw) {
        var parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) legacy = parsed;
      }
    } catch (e) {
      legacy = [];
    }

    var merged = existing.slice();
    for (var i = 0; i < legacy.length; i++) {
      var row = legacy[i];
      if (!row || typeof row !== 'object') continue;
      var vid = firstValue(row, ['ProductVariantId', 'productVariantId', 'VariantId', 'variantId']);
      if (!vid) continue;
      vid = String(vid);
      if (findVid(merged, vid) !== -1) continue;
      var pid = firstValue(row, ['ProductId', 'productId', 'Id', 'id']);
      var link = firstValue(row, ['ProductLink', 'productLink', 'ProductUrl', 'productUrl']);
      merged.push({
        vid: vid,
        pid: pid == null ? '' : String(pid),
        handle: handleFromLink(link),
        at: legacyDate(firstValue(row, ['AddedOn', 'addedOn']))
      });
    }

    return merged;
  }

  function firstValue(obj, keys) {
    for (var i = 0; i < keys.length; i++) {
      var v = obj[keys[i]];
      if (v !== undefined && v !== null && v !== '') return v;
    }
    return null;
  }

  function findVid(list, vid) {
    for (var i = 0; i < list.length; i++) {
      if (list[i].vid === vid) return i;
    }
    return -1;
  }

  function handleFromLink(link) {
    if (!link) return '';
    var s = String(link).split('?')[0].split('#')[0];
    var marker = '/products/';
    var at = s.indexOf(marker);
    if (at === -1) return '';
    var rest = s.slice(at + marker.length).replace(/\/+$/, '');
    return rest.split('/')[0] || '';
  }

  function legacyDate(value) {
    if (!value) return nowIso();
    var d = new Date(String(value));
    if (isNaN(d.getTime())) return nowIso();
    return d.toISOString();
  }

  function nowIso() {
    return new Date().toISOString();
  }

  /* ------------------------------------------------------------ proxy */

  function proxy(path, payload) {
    return fetch(proxyBase + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (!response.ok) return { ok: false, error: 'http_' + response.status };
      return response.json().catch(function () { return { ok: false, error: 'parse' }; });
    }).catch(function () {
      return { ok: false, error: 'network' };
    });
  }

  function proxyGet(path) {
    return fetch(proxyBase + path, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) return { ok: false, error: 'http_' + response.status };
      return response.json().catch(function () { return { ok: false, error: 'parse' }; });
    }).catch(function () {
      return { ok: false, error: 'network' };
    });
  }

  /* -------------------------------------------------------- public API */

  function has(vid) {
    return indexOfVid(String(vid)) !== -1;
  }

  function add(item) {
    var vid = item && item.vid ? String(item.vid) : '';
    if (!vid || has(vid)) return Promise.resolve({ ok: true, items: state.slice() });

    var entry = {
      vid: vid,
      pid: item.pid == null ? '' : String(item.pid),
      handle: item.handle == null ? '' : String(item.handle),
      at: nowIso(),
      list: cleanLabel(item.list)
    };

    if (!customerId) {
      setState(state.concat([entry]));
      return Promise.resolve({ ok: true, items: state.slice() });
    }

    // Optimistic: the button should not wait on the round trip. Any failure
    // rolls the add back, exactly as remove() rolls a failed removal back, so
    // the button, the count badge and the error note never disagree.
    var previous = state.slice();
    setState(state.concat([entry]));
    return proxy('/add', {
      vid: entry.vid,
      pid: entry.pid,
      handle: entry.handle,
      list: entry.list
    }).then(function (res) {
      if (res && res.ok && Array.isArray(res.items)) {
        setState(mergeHandles(res.items));
      } else {
        setState(previous);
      }
      return res || { ok: false, error: 'network' };
    });
  }

  function remove(vid) {
    vid = String(vid);
    if (!has(vid)) return Promise.resolve({ ok: true, items: state.slice() });

    var previous = state.slice();
    setState(state.filter(function (row) { return row.vid !== vid; }));
    if (!customerId) return Promise.resolve({ ok: true, items: state.slice() });

    return proxy('/remove', { vid: vid }).then(function (res) {
      if (res && res.ok && Array.isArray(res.items)) {
        setState(mergeHandles(res.items));
      } else {
        setState(previous);
      }
      return res || { ok: false, error: 'network' };
    });
  }

  /*
    Set (or clear, with "") the list label on one saved row. Optimistic like
    add/remove: the select must not wait on the round trip, and a failure rolls
    the label back so the chip and the server never disagree. A guest's label
    lives in localStorage, so setState is the whole write.
  */
  function setLabel(vid, label) {
    vid = String(vid);
    var index = indexOfVid(vid);
    if (index === -1) return Promise.resolve({ ok: true, items: state.slice() });

    var clean = cleanLabel(label);
    if (state[index].list === clean) return Promise.resolve({ ok: true, items: state.slice() });

    var previous = state.slice();
    var next = state.map(function (row) {
      if (row.vid !== vid) return row;
      return { vid: row.vid, pid: row.pid, handle: row.handle, at: row.at, list: clean };
    });
    setState(next);
    if (!customerId) return Promise.resolve({ ok: true, items: state.slice() });

    return proxy('/label', { vid: vid, list: clean }).then(function (res) {
      /*
        Signed in, the grid is rebuilt twice: once optimistically and once when
        the server answers. The first rebuild has already spent the focus
        ticket, so re-arm it or the keyboard is dropped by the second.
      */
      focusListVid = vid;
      if (res && res.ok && Array.isArray(res.items)) {
        setState(mergeHandles(res.items));
      } else {
        setState(previous);
      }
      return res || { ok: false, error: 'network' };
    });
  }

  /*
    The server only stores vid/pid/at/list. Handles we already know (from the
    server-rendered config or a guest entry) are worth keeping so the guest
    renderer and any later lookup do not have to refetch them.
  */
  function mergeHandles(serverItems) {
    var byVid = {};
    for (var i = 0; i < state.length; i++) byVid[state[i].vid] = state[i].handle;
    return serverItems.map(function (row) {
      var vid = String(row.vid == null ? '' : row.vid);
      return {
        vid: vid,
        pid: row.pid == null ? '' : String(row.pid),
        handle: row.handle || byVid[vid] || '',
        at: row.at == null ? '' : String(row.at),
        list: cleanLabel(row.list)
      };
    });
  }

  window.HeatWishlist = {
    has: has,
    add: add,
    remove: remove,
    setList: setLabel,
    items: function () { return state.slice(); },
    isLoggedIn: function () { return !!customerId; },
    isSharedView: function () { return sharedMode; },
    pageUrl: pageUrl
  };

  /* ------------------------------------------------------------ merge */

  function mergeGuestIntoAccount() {
    if (!customerId) return;
    var guest = readGuest();
    if (!guest.length) return;
    if (legacyMergedIntoAccount()) {
      // The legacy Wishlist Hero key was already merged into this account once.
      // A guest session on the same browser re-imports that key into the guest
      // list, so rows still present in the legacy key are NOT merged again:
      // otherwise an item the customer removed from the account would return.
      var legacyVids = importLegacy([]).map(function (row) { return row.vid; });
      guest = guest.filter(function (row) { return legacyVids.indexOf(String(row.vid)) === -1; });
      if (!guest.length) { clearGuest(); return; }
    }
    postMerge(guest);
  }

  /*
    The account list is uncapped, so a merge either lands or it does not. On
    success the guest list is cleared; on any failure it is left alone and the
    next page load tries again.
  */
  function postMerge(guest) {
    proxy('/merge', {
      items: guest.map(function (row) {
        return {
          vid: row.vid,
          pid: row.pid,
          handle: row.handle,
          at: row.at || nowIso(),
          list: cleanLabel(row.list)
        };
      })
    }).then(function (res) {
      if (res && res.ok && Array.isArray(res.items)) {
        // Keep the guest handles: the server does not store them.
        var byVid = {};
        for (var i = 0; i < guest.length; i++) byVid[guest[i].vid] = guest[i].handle;
        setState(res.items.map(function (row) {
          var vid = String(row.vid == null ? '' : row.vid);
          return {
            vid: vid,
            pid: row.pid == null ? '' : String(row.pid),
            handle: row.handle || byVid[vid] || '',
            at: row.at == null ? '' : String(row.at),
            list: cleanLabel(row.list)
          };
        }));
        clearGuest();
        markLegacyMergedIntoAccount();
      }
    });
  }

  /* ----------------------------------------------------------- buttons */

  function scopeOf(el) {
    return el.closest('[data-product-wrapper]') || document;
  }

  function currentVariantId(scope) {
    var vs = scope.querySelector('[data-variant-selection]');
    if (vs && vs.getAttribute('variant')) return vs.getAttribute('variant');
    var input = scope.querySelector('[name="id"]');
    return input ? String(input.value || '') : '';
  }

  /*
    Runs on every body mutation (see observeBody), so it must leave the DOM
    alone when nothing changed. Each write below is compared first: assigning
    textContent replaces the text node even when the text is identical, and
    that replacement is itself a body mutation, so an unguarded write here
    re-queues this scan forever (it ran about 60 times a second, on every
    product page, before the writes were guarded).
  */
  function refreshButtons() {
    var buttons = document.querySelectorAll('[data-heat-wishlist-button]');
    for (var i = 0; i < buttons.length; i++) {
      var button = buttons[i];
      var vid = currentVariantId(scopeOf(button));
      setAttr(button, 'data-vid', vid);
      var saved = vid ? has(vid) : false;
      if (button.classList.contains('is-saved') !== saved) button.classList.toggle('is-saved', saved);
      setAttr(button, 'aria-pressed', saved ? 'true' : 'false');
      var label = button.querySelector('[data-heat-wishlist-label]');
      setText(label, saved ? 'Saved to wishlist' : 'Save to wishlist');
      /*
        The "View wishlist" link only makes sense once there is something on
        the wishlist to view, so it follows the saved state of THIS variant,
        on first paint and on every toggle.
      */
      var wrap = button.closest ? button.closest('.heat-wishlist-save') : null;
      var view = wrap ? wrap.querySelector('[data-heat-wishlist-view]') : null;
      if (view && view.hidden !== !saved) view.hidden = !saved;
    }
  }

  /* Write-if-different helpers: see the note on refreshButtons. */
  function setText(node, text) {
    if (node && node.textContent !== text) node.textContent = text;
  }

  function setAttr(node, name, value) {
    if (node && node.getAttribute(name) !== value) node.setAttribute(name, value);
  }

  /*
    One delegated listener on document, never a per-button binding: quick shop
    is on by default and renders product-form (and so this button) into a modal
    long after init ran, and a modal button that no one bound is a dead button.
  */
  function bindButtons() {
    if (document.__heatWishlistBound) return;
    document.__heatWishlistBound = true;
    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var button = target.closest('[data-heat-wishlist-button]');
      if (button) onButtonClick(event, button);
    });
  }

  function onButtonClick(event, button) {
    event.preventDefault();
    if (busy) return;
    var vid = currentVariantId(scopeOf(button));
    if (!vid) return;
    busy = true;
    button.disabled = true;
    var action = has(vid)
      ? remove(vid)
      : add({
        vid: vid,
        pid: button.getAttribute('data-pid') || '',
        handle: button.getAttribute('data-handle') || ''
      });
    action.then(function (res) {
      busy = false;
      button.disabled = false;
      var wrap = button.closest ? button.closest('.heat-wishlist-save') : null;
      var note = wrap ? wrap.querySelector('[data-heat-wishlist-error]') : null;
      if (note) {
        if (res && res.ok === false) {
          note.textContent = 'Could not save that just now. Please try again.';
          note.hidden = false;
        } else {
          note.hidden = true;
        }
      }
    });
  }

  function observeSelections() {
    var nodes = document.querySelectorAll('[data-variant-selection]');
    for (var i = 0; i < nodes.length; i++) {
      var vs = nodes[i];
      if (vs.__heatWishlistObserved) continue;
      vs.__heatWishlistObserved = true;
      new MutationObserver(refreshButtons)
        .observe(vs, { attributes: true, attributeFilter: ['variant'] });
    }
  }

  /*
    Quick shop injects a whole product form into a modal. Watch the body for
    new nodes and re-scan once per frame: cheap, and it is what gives a modal
    button its variant id and its saved state.

    The rule that keeps this from looping: a scan must not change the DOM when
    nothing changed. Any node it adds, removes or replaces (a textContent
    assignment counts, even with the same text) is a body mutation, which
    queues the next scan, which writes again. observeSelections only attaches
    observers and refreshButtons only writes values that differ, so an idle
    page does no work here.
  */
  /*
    rAF plus a timeout, first one wins. A hidden or background tab never paints
    a frame, so rAF alone can sit unfired for as long as the shopper is looking
    at something else, and the work here has to happen anyway.
  */
  function nextFrame(fn) {
    var done = false;
    var run = function () {
      if (done) return;
      done = true;
      fn();
    };
    if (window.requestAnimationFrame) window.requestAnimationFrame(run);
    setTimeout(run, 24);
  }

  var scanQueued = false;

  function queueScan() {
    if (scanQueued) return;
    scanQueued = true;
    nextFrame(function () {
      scanQueued = false;
      observeSelections();
      refreshButtons();
    });
  }

  function observeBody() {
    if (!window.MutationObserver || !document.body) return;
    new MutationObserver(queueScan)
      .observe(document.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------- count */

  /*
    The count sits INSIDE the header heart. With anything saved the heart link
    gets .is-active, which swaps the outline path for the filled one (brand
    red, snippets/custom-wishlist.liquid) and the number paints white on top of
    it. With nothing saved the number is hidden and the heart is an outline
    again. Both hearts, mobile and desktop, carry [data-heat-wishlist-count].
  */
  /*
    The number itself is aria-hidden, so screen readers get the count from the
    link's own label instead: "Wishlist, 3 saved", or plain "Wishlist" at zero.
    Every write is compared first, for the same reason as refreshButtons: this
    runs on every state change and must not churn the body observer.
  */
  function refreshCounts() {
    var count = state.length;
    var badges = document.querySelectorAll('[data-heat-wishlist-count]');
    for (var i = 0; i < badges.length; i++) {
      var badge = badges[i];
      setText(badge, count ? String(count) : '');
      if (badge.hidden !== (count === 0)) badge.hidden = count === 0;
      var link = badge.closest ? badge.closest('a.wishlist') : null;
      if (!link) continue;
      if (link.classList.contains('is-active') !== (count > 0)) link.classList.toggle('is-active', count > 0);
      setAttr(link, 'aria-label', count > 0 ? 'Wishlist, ' + count + ' saved' : 'Wishlist');
    }
  }

  /* -------------------------------------------------- light card render */

  /*
    One promise per handle, cached for the life of the page, so re-renders
    (filter, sort, remove) never refetch. The lookups themselves go through a
    small queue with at most FETCH_LIMIT in flight: the biggest lists are 300+
    rows, and firing every /products/<handle>.js at once floods the storefront
    and risks throttled answers, which would draw live bottles as "No longer
    available". Each lookup resolves to the product or null, never rejects, so
    the queue always moves on.
  */
  var productCache = {};
  var FETCH_LIMIT = 12;
  var fetchQueue = [];
  var fetchActive = 0;

  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = new Promise(function (resolve) {
      fetchQueue.push(function () {
        fetch('/products/' + encodeURIComponent(handle) + '.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        }).then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; })
          .then(function (product) {
            fetchActive--;
            resolve(product);
            pumpFetches();
          });
      });
      pumpFetches();
    });
    return productCache[handle];
  }

  function pumpFetches() {
    while (fetchActive < FETCH_LIMIT && fetchQueue.length) {
      fetchActive++;
      fetchQueue.shift()();
    }
  }

  function money(cents) {
    if (typeof cents !== 'number') return '';
    return '$' + (cents / 100).toFixed(2);
  }

  /* ----------------------------------------------------- card images */

  /*
    /products/<handle>.js hands back the ORIGINAL upload (700 to 2000+ px),
    while a card is at most a quarter of a 1200px row. Every card image asks
    Shopify's CDN for a sized copy instead: width=<n> on the same URL, with a
    srcset the browser picks from for the slot it actually lays out at 1x, 2x
    or 3x.

    sizes mirrors the grid in sections/heat-wishlist.liquid: .heat-wishlist is
    max 1200px wide with 20px side padding (border-box, so 1160px of content);
    2 columns with a 10px gap under 720px, 3 columns with a 10px gap to 859px,
    then the section's "Products per row" setting with a 20px gap. The grid
    carries that setting as data-columns; without it (an older copy of the
    section) the default of 4 is assumed. Change the grid CSS and this
    together, or the browser fetches the wrong size.
  */
  var CARD_WIDTHS = [200, 300, 400, 600, 800];
  var CARD_SRC_WIDTH = 400;

  function sizedImageUrl(src, width) {
    var url = String(src || '');
    if (!url) return '';
    /*
      Plain string work, not new URL(): the payload's URLs are protocol
      relative ("//cdn.shopify.com/..."), which URL() rejects without a base,
      and they usually carry ?v= already. Any width= already present is
      replaced, never doubled.
    */
    var hash = '';
    var hashAt = url.indexOf('#');
    if (hashAt !== -1) {
      hash = url.slice(hashAt);
      url = url.slice(0, hashAt);
    }
    url = url.replace(/([?&])width=[^&]*(&|$)/, function (match, lead, tail) {
      return tail ? lead : '';
    });
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width + hash;
  }

  function cardSizes(list) {
    var columns = parseInt(list && list.getAttribute('data-columns'), 10);
    if (!(columns >= 1 && columns <= 6)) columns = 4;
    var gaps = (columns - 1) * 20;
    return '(min-width: 1200px) ' + Math.floor((1160 - gaps) / columns) + 'px, '
      + '(min-width: 860px) calc((100vw - ' + (40 + gaps) + 'px) / ' + columns + '), '
      + '(min-width: 720px) calc((100vw - 60px) / 3), '
      + 'calc((100vw - 50px) / 2)';
  }

  /*
    Intrinsic size of the featured image, from the media entry with the same
    URL, so the width and height attributes reserve the real shape and nothing
    shifts when the image arrives (the CSS rule sets height: auto, as
    THEME_GOTCHAS requires). Square is the fallback: most bottle shots are.
  */
  function featuredImageSize(product) {
    var wanted = stripScheme(product.featured_image);
    var media = Array.isArray(product.media) ? product.media : [];
    for (var i = 0; i < media.length; i++) {
      var preview = media[i] && media[i].preview_image;
      if (preview && preview.width && preview.height && stripScheme(preview.src) === wanted) {
        return { width: preview.width, height: preview.height };
      }
    }
    return { width: CARD_SRC_WIDTH, height: CARD_SRC_WIDTH };
  }

  function stripScheme(url) {
    return String(url || '').replace(/^https?:/, '');
  }

  function cardImage(product) {
    var img = document.createElement('img');
    img.className = 'heat-wishlist__card-image';
    var size = featuredImageSize(product);
    img.width = size.width;
    img.height = size.height;
    img.alt = product.title;
    img.loading = 'lazy';
    img.sizes = cardSizes(document.querySelector('[data-heat-wishlist-list]'));
    img.srcset = CARD_WIDTHS.map(function (w) {
      return sizedImageUrl(product.featured_image, w) + ' ' + w + 'w';
    }).join(', ');
    img.src = sizedImageUrl(product.featured_image, CARD_SRC_WIDTH);
    return img;
  }

  /*
    Every render takes a ticket. The product lookups are async, so a render
    started before a removal can resolve after it and put the removed card
    back; a stale ticket makes that late resolution a no-op.
  */
  var pageTicket = 0;

  /*
    THE page renderer, and the only one: logged-in shoppers and guests get the
    same light cards from the same state, so the two never look or behave
    differently. Every row is rendered, including the ones with no handle and
    the ones whose product lookup fails: they still count toward the badge, so
    silently dropping them would leave a shopper unable to clear a row they can
    see in the count. Those get a minimal card with a working Remove instead.
  */
  function renderPage() {
    if (sharedMode) return;
    var page = document.querySelector('.heat-wishlist');
    if (!page) return;
    var list = page.querySelector('[data-heat-wishlist-list]');
    if (!list) return;

    pageTicket++;
    var ticket = pageTicket;
    renderTools(page);
    updatePageCount(page, 0);

    if (state.length === 0) {
      list.innerHTML = '';
      return;
    }

    var rows = visibleRows();
    Promise.all(rows.map(function (row) {
      if (!row.handle) return Promise.resolve({ row: row, product: null, resolved: null });
      return fetchProduct(row.handle).then(function (product) {
        return { row: row, product: product, resolved: resolveRow(product, row.vid) };
      });
    })).then(function (results) {
      if (ticket !== pageTicket) return;
      sortResults(results);
      list.innerHTML = '';
      results.forEach(function (result) {
        if (!result.resolved) {
          list.appendChild(unavailableCard(result.row));
          return;
        }
        list.appendChild(lightCard(result.row, result.product, result.resolved));
      });
      if (!results.length) list.appendChild(emptyFilterNote());
      updatePageCount(page, results.length);
      restoreListFocus(page);
    });
  }

  /* --------------------------------------------------- filter and sort */

  function sameLabel(a, b) {
    return String(a || '').toLowerCase() === String(b || '').toLowerCase();
  }

  function hasLabel(labels, label) {
    for (var i = 0; i < labels.length; i++) {
      if (sameLabel(labels[i], label)) return true;
    }
    return false;
  }

  /* The rows the grid draws: everything, or just one label's worth. */
  function visibleRows() {
    if (!viewFilter) return state.slice();
    return state.filter(function (row) { return sameLabel(row.list, viewFilter); });
  }

  function dateValue(at) {
    var t = Date.parse(String(at || ''));
    return isNaN(t) ? 0 : t;
  }

  /*
    Heat ordinal from product tags, the same rules
    sections/collection-heat-bands.liquid uses for its tag fallback: exact,
    case-insensitive, hottest wins. 0 means untagged, which sorts last because
    the heat sort is descending. The canonical custom.heatlevel metafield is
    NOT in the /products/<handle>.js payload, so tags are all there is here.
  */
  function heatRank(product) {
    if (!product || !Array.isArray(product.tags)) return 0;
    var rank = 0;
    for (var i = 0; i < product.tags.length; i++) {
      var tag = String(product.tags[i] == null ? '' : product.tags[i]).replace(/^\s+|\s+$/g, '').toLowerCase();
      var value = 0;
      if (tag === 'extreme') value = 5;
      else if (tag === 'extra-hot' || tag === 'extra hot' || tag === 'extrahot') value = 4;
      else if (tag === 'hot') value = 3;
      else if (tag === 'medium') value = 2;
      else if (tag === 'mild') value = 1;
      if (value > rank) rank = value;
    }
    return rank;
  }

  /*
    Sorts the resolved results in place. Everything is decided from data the
    page already fetched (/products/<handle>.js) plus the saved timestamp, so
    changing the sort never costs a request. Rows whose product could not be
    resolved always land at the end, whatever the sort, and newest-first is the
    tiebreak under every other order so the result is stable.
  */
  function sortResults(results) {
    var decorated = results.map(function (result, index) {
      var variant = result.resolved ? result.resolved.variant : null;
      var product = result.product;
      var compare = variant && typeof variant.compare_at_price === 'number' ? variant.compare_at_price : 0;
      var price = variant && typeof variant.price === 'number' ? variant.price : 0;
      return {
        result: result,
        index: index,
        ready: !!variant,
        time: dateValue(result.row.at),
        price: price,
        vendor: product && product.vendor ? String(product.vendor).toLowerCase() : '',
        heat: heatRank(product),
        sale: compare > price ? 1 : 0,
        stock: variant && variant.available ? 1 : 0
      };
    });

    var comparators = {
      date: function () { return 0; },
      'price-asc': function (a, b) { return a.price - b.price; },
      'price-desc': function (a, b) { return b.price - a.price; },
      brand: function (a, b) { return a.vendor < b.vendor ? -1 : (a.vendor > b.vendor ? 1 : 0); },
      heat: function (a, b) { return b.heat - a.heat; },
      sale: function (a, b) { return b.sale - a.sale; },
      stock: function (a, b) { return b.stock - a.stock; }
    };
    var chosen = comparators[viewSort] || comparators.date;

    decorated.sort(function (a, b) {
      if (a.ready !== b.ready) return a.ready ? -1 : 1;
      if (a.ready && b.ready) {
        var primary = chosen(a, b);
        if (primary) return primary;
      }
      if (b.time !== a.time) return b.time - a.time;
      return a.index - b.index;
    });

    for (var i = 0; i < decorated.length; i++) results[i] = decorated[i].result;
  }

  function emptyFilterNote() {
    var note = document.createElement('p');
    note.className = 'heat-wishlist__none';
    note.textContent = 'Nothing saved in that list yet.';
    return note;
  }

  /*
    The toolbar above the grid: one chip per label in use (plus All), and the
    sort select. Chips are hidden outright while no label exists, so a shopper
    who never uses lists never sees the feature.
  */
  function renderTools(page) {
    var tools = page.querySelector('[data-heat-wishlist-tools]');
    if (!tools) return;

    var labels = labelsInUse();
    if (viewFilter && !hasLabel(labels, viewFilter)) {
      viewFilter = '';
      writePref(FILTER_KEY, '');
    }

    tools.hidden = state.length === 0;

    var filters = page.querySelector('[data-heat-wishlist-filters]');
    if (filters) {
      filters.innerHTML = '';
      filters.hidden = labels.length === 0;
      if (labels.length) {
        filters.appendChild(filterChip('All', ''));
        for (var i = 0; i < labels.length; i++) filters.appendChild(filterChip(labels[i], labels[i]));
      }
    }

    var newList = page.querySelector('[data-heat-wishlist-new-list]');
    if (newList) newList.textContent = listsInUse() ? 'New list' : 'Create a list';

    var sort = page.querySelector('[data-heat-wishlist-sort]');
    if (sort && sort.value !== viewSort) sort.value = viewSort;
  }

  function filterChip(text, value) {
    var on = sameLabel(value, viewFilter);
    var chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'heat-wishlist__chip' + (on ? ' is-on' : '');
    chip.setAttribute('data-heat-wishlist-filter', value);
    chip.setAttribute('aria-pressed', on ? 'true' : 'false');
    chip.textContent = text;
    return chip;
  }

  /*
    Option values are namespaced so a label can never collide with a control:
    'none' clears the label, 'new' opens the prompt, 'L:<label>' is a real one.
  */
  function listOption(value, text) {
    var option = document.createElement('option');
    option.value = value;
    option.textContent = text;
    return option;
  }

  function listValueFor(label) {
    return label ? 'L:' + label : 'none';
  }

  function currentListValue(vid) {
    var index = indexOfVid(String(vid));
    return listValueFor(index === -1 ? '' : state[index].list);
  }

  function listSelect(row, title) {
    var select = document.createElement('select');
    select.className = 'heat-wishlist__select';
    select.setAttribute('data-heat-wishlist-list-select', row.vid);
    select.setAttribute('aria-label', 'List for ' + (title || 'this item'));

    var labels = labelsOffered();
    select.appendChild(listOption('none', 'No list'));
    for (var i = 0; i < labels.length; i++) {
      select.appendChild(listOption(listValueFor(labels[i]), labels[i]));
    }
    select.appendChild(listOption('new', 'New list...'));
    var canonical = row.list;
    for (var j = 0; j < labels.length; j++) {
      if (labels[j].toLowerCase() === String(row.list || '').toLowerCase()) { canonical = labels[j]; break; }
    }
    select.value = listValueFor(canonical);
    return select;
  }

  /*
    Brand line: the product's vendor, on its own line directly under the sauce
    name and ahead of the variant title and price. Skipped when the vendor is
    empty, and when it is only the product title again, where it would repeat
    the line above it word for word.
  */
  function brandLine(product) {
    var vendor = product && product.vendor ? String(product.vendor).replace(/^\s+|\s+$/g, '') : '';
    if (!vendor) return null;
    var title = product && product.title ? String(product.title).replace(/^\s+|\s+$/g, '') : '';
    if (vendor.toLowerCase() === title.toLowerCase()) return null;
    var el = document.createElement('span');
    el.className = 'heat-wishlist__card-brand';
    el.textContent = vendor;
    return el;
  }

  function listChip(label) {
    var chip = document.createElement('span');
    chip.className = 'heat-wishlist__card-chip';
    chip.textContent = label;
    return chip;
  }

  /*
    Changing a label rebuilds the whole grid (the chips and every select change
    with it), which would otherwise drop the keyboard back to the top of the
    document. Put focus back on the select that was just used.
  */
  function restoreListFocus(page) {
    if (!focusListVid) return;
    var vid = focusListVid;
    focusListVid = '';
    var select = page.querySelector('[data-heat-wishlist-list-select="' + cssEscape(vid) + '"]');
    if (select && select.focus) select.focus();
  }

  function cssEscape(value) {
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function findVariant(product, vid) {
    if (!product || !Array.isArray(product.variants)) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (String(product.variants[i].id) === vid) return product.variants[i];
    }
    return null;
  }

  /*
    Resolution rule for a light card:
      a) the saved variant still exists -> use it;
      b) the product exists but the saved variant is gone -> use its first
         available variant (or the first variant when nothing is in stock) and
         flag it, so the card can say "Product updated" and add the CURRENT
         variant to cart rather than a dead id;
      c) no product at all -> null, and the caller draws the
         "No longer available" card.
    Remove always removes the SAVED vid, whichever branch ran.
  */
  function resolveRow(product, vid) {
    if (!product || !Array.isArray(product.variants) || !product.variants.length) return null;
    var exact = findVariant(product, vid);
    if (exact) return { variant: exact, substituted: false };
    var pick = null;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) { pick = product.variants[i]; break; }
    }
    if (!pick) pick = product.variants[0];
    return { variant: pick, substituted: true };
  }

  /*
    No handle, or a handle that no longer resolves to a product. Nothing can be
    shown but a working Remove.
  */
  function unavailableCard(row) {
    var card = document.createElement('div');
    card.className = 'heat-wishlist__card';
    card.setAttribute('data-heat-wishlist-card', row.vid);

    var title = document.createElement('span');
    title.className = 'heat-wishlist__card-title';
    title.textContent = 'No longer available';
    card.appendChild(title);

    var actions = document.createElement('div');
    actions.className = 'heat-wishlist__card-actions';

    var removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'heat-wishlist__btn heat-wishlist__btn--ghost';
    removeBtn.setAttribute('data-heat-wishlist-remove', row.vid);
    removeBtn.textContent = 'Remove';
    actions.appendChild(removeBtn);

    card.appendChild(actions);
    return card;
  }

  function lightCard(row, product, resolved) {
    var variant = resolved.variant;

    var card = document.createElement('div');
    card.className = 'heat-wishlist__card';
    card.setAttribute('data-heat-wishlist-card', row.vid);
    /*
      What "Add all to cart" will add for this row: the CURRENT variant, which
      for a substituted row is not the saved vid. Case (c) cards carry neither
      attribute, so add-all skips them without counting them as sold out.
    */
    card.setAttribute('data-heat-wishlist-addable', String(variant.id));
    card.setAttribute('data-available', variant.available ? 'true' : 'false');

    var link = document.createElement('a');
    link.className = 'heat-wishlist__card-link';
    link.href = '/products/' + product.handle + '?variant=' + variant.id;

    if (product.featured_image) link.appendChild(cardImage(product));

    var title = document.createElement('span');
    title.className = 'heat-wishlist__card-title';
    title.textContent = product.title;
    link.appendChild(title);
    card.appendChild(link);

    var brand = brandLine(product);
    if (brand) card.appendChild(brand);

    if (variant.title && variant.title !== 'Default Title') {
      var vt = document.createElement('span');
      vt.className = 'heat-wishlist__card-variant';
      vt.textContent = variant.title;
      card.appendChild(vt);
    }

    var price = document.createElement('span');
    price.className = 'heat-wishlist__card-price';
    price.textContent = money(variant.price);
    card.appendChild(price);

    if (resolved.substituted) {
      var note = document.createElement('span');
      note.className = 'heat-wishlist__card-note';
      note.textContent = 'Product updated';
      card.appendChild(note);
    }

    if (row.list) card.appendChild(listChip(row.list));

    if (listsInUse()) {
      var listRow = document.createElement('div');
      listRow.className = 'heat-wishlist__card-list';
      listRow.appendChild(listSelect(row, product.title));
      card.appendChild(listRow);
    }

    var actions = document.createElement('div');
    actions.className = 'heat-wishlist__card-actions';

    var atc = document.createElement('button');
    atc.type = 'button';
    atc.className = 'heat-wishlist__btn heat-wishlist__btn--primary';
    atc.textContent = variant.available ? 'Add to cart' : 'Sold out';
    atc.disabled = !variant.available;
    if (variant.available) {
      atc.addEventListener('click', function () {
        addToCart([{ id: variant.id, quantity: 1 }], atc, 'Add to cart');
      });
    }
    actions.appendChild(atc);

    var removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'heat-wishlist__btn heat-wishlist__btn--ghost';
    removeBtn.setAttribute('data-heat-wishlist-remove', row.vid);
    removeBtn.textContent = 'Remove';
    actions.appendChild(removeBtn);

    card.appendChild(actions);
    return card;
  }

  /* -------------------------------------------------------------- cart */

  function statusNode() {
    return document.querySelector('[data-heat-wishlist-status]');
  }

  function say(message) {
    var node = statusNode();
    if (!node) return;
    node.textContent = message;
    node.hidden = !message;
  }

  /*
    One request, never a fan-out: concurrent cart mutations are read-modify-
    write on Shopify's side and silently lose one another.
  */
  function addToCart(items, button, restoreLabel, note) {
    note = note || '';
    if (!items.length) {
      say('Nothing to add. Everything saved here is sold out.');
      return;
    }
    if (!window.HeatCart) {
      say('Cart is still loading. Please try again in a moment.');
      return;
    }
    if (button) {
      button.disabled = true;
      button.textContent = 'Adding...';
    }
    window.HeatCart.addToCart({ items: items }).then(function (response) {
      if (button) {
        button.disabled = false;
        button.textContent = restoreLabel;
      }
      if (!response.ok) {
        say('Could not add to cart. Some items may be unavailable.');
        return;
      }
      return window.HeatCart.getCart().then(function (cart) {
        if (cart) {
          var badges = document.querySelectorAll('[data-header-cart-count]');
          for (var i = 0; i < badges.length; i++) {
            badges[i].setAttribute('data-header-cart-count', cart.item_count);
            badges[i].classList.toggle('visible', cart.item_count > 0);
          }
        }
        say(items.length === 1
          ? 'Added to cart' + note + '.'
          : 'Added ' + items.length + ' items to cart' + note + '.');
      });
    }).catch(function () {
      if (button) {
        button.disabled = false;
        button.textContent = restoreLabel;
      }
      say('Could not add to cart. Please try again.');
    });
  }

  function bindPageControls() {
    var page = document.querySelector('.heat-wishlist');
    if (!page || page.__heatWishlistBound) return;
    page.__heatWishlistBound = true;

    page.addEventListener('click', function (event) {
      var removeBtn = event.target.closest('[data-heat-wishlist-remove]');
      if (removeBtn) {
        event.preventDefault();
        removeBtn.disabled = true;
        /*
          One card is one saved variant id now, but the control still parses a
          comma list and removes in turn, never in parallel: a fan-out of
          proxy writes is the same read-modify-write race as a cart fan-out.
        */
        var vids = (removeBtn.getAttribute('data-heat-wishlist-remove') || '')
          .split(',')
          .filter(Boolean);
        var card = removeBtn.closest('[data-heat-wishlist-card]');
        var cardIndex = indexOfCard(page, card);
        vids.reduce(function (chain, vid) {
          return chain.then(function (results) {
            return remove(vid).then(function (res) {
              results.push(res);
              return results;
            });
          });
        }, Promise.resolve([])).then(function (results) {
          var allOk = results.every(function (res) { return res && res.ok !== false; });
          if (!allOk) {
            // The server still holds the row. Leave the card where it is.
            removeBtn.disabled = false;
            say('Could not remove that just now. Please try again.');
            return;
          }
          if (card && card.parentNode) card.parentNode.removeChild(card);
          say('');
          updatePageCount(page, page.querySelectorAll('[data-heat-wishlist-card]').length);
          focusAfterRemove(page, cardIndex);
        });
        return;
      }

      /*
        Create a list. The only way in while no label exists: it makes the
        label, which makes listsInUse() true, which is what puts a List select
        on every card. Cancelling, or typing nothing but spaces, changes
        nothing at all.
      */
      var newList = event.target.closest('[data-heat-wishlist-new-list]');
      if (newList) {
        event.preventDefault();
        var typedLabel = cleanLabel(window.prompt('Name this list', ''));
        if (!typedLabel) return;
        if (!hasLabel(labelsOffered(), typedLabel)) newLabels.push(typedLabel);
        renderPage();
        say('List "' + typedLabel + '" created. Use the List menu under a sauce to add it to this list.');
        return;
      }

      var chip = event.target.closest('[data-heat-wishlist-filter]');
      if (chip) {
        event.preventDefault();
        viewFilter = chip.getAttribute('data-heat-wishlist-filter') || '';
        writePref(FILTER_KEY, viewFilter);
        say('');
        renderPage();
        return;
      }

      var addAll = event.target.closest('[data-heat-wishlist-add-all]');
      if (addAll) {
        event.preventDefault();
        collectAddAll(page).then(function (payload) {
          if (payload.skipped) {
            say(payload.skipped === 1
              ? '1 saved item is sold out and was skipped.'
              : payload.skipped + ' saved items are sold out and were skipped.');
          }
          /*
            Add all adds what is ON SCREEN, so with a filter on it adds that
            list and nothing else. The status line has to say which list, or
            the shopper cannot tell a filtered add from a whole-wishlist add.
          */
          addToCart(payload.items, addAll, 'Add all to cart',
            viewFilter ? ' from your ' + viewFilter + ' list' : '');
        });
      }
    });

    /*
      One delegated change listener for the per-card List selects. Every label
      write goes through here: the select is the only control that sets one.
    */
    page.addEventListener('change', function (event) {
      var select = event.target.closest
        ? event.target.closest('[data-heat-wishlist-list-select]')
        : null;
      if (!select) return;

      var vid = select.getAttribute('data-heat-wishlist-list-select') || '';
      var raw = select.value;
      var label;

      if (raw === 'new') {
        var typed = window.prompt('Name this list', '');
        label = cleanLabel(typed);
        if (!label) {
          // Cancelled, or nothing but spaces. Put the select back as it was.
          select.value = currentListValue(vid);
          return;
        }
      } else if (raw === 'none') {
        label = '';
      } else {
        label = raw.slice(2);
      }

      focusListVid = vid;
      setLabel(vid, label).then(function (res) {
        say(res && res.ok === false
          ? 'Could not update that list just now. Please try again.'
          : '');
      });
    });

    var sortSelect = page.querySelector('[data-heat-wishlist-sort]');
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        var value = sortSelect.value;
        viewSort = SORT_VALUES.indexOf(value) === -1 ? 'date' : value;
        writePref(SORT_KEY, viewSort);
        renderPage();
      });
    }
  }

  function indexOfCard(page, card) {
    if (!card) return -1;
    var cards = page.querySelectorAll('[data-heat-wishlist-card]');
    for (var i = 0; i < cards.length; i++) {
      if (cards[i] === card) return i;
    }
    return -1;
  }

  /*
    Removing a card destroys the control that had focus, which drops the
    keyboard back to the top of the document. Move focus to the Remove of the
    card that took its place, or to the count line when it was the last card.
    Deferred a frame: the guest branch rebuilds its whole list asynchronously.
  */
  function focusAfterRemove(page, cardIndex) {
    nextFrame(function () {
      var buttons = page.querySelectorAll('[data-heat-wishlist-card] [data-heat-wishlist-remove]');
      var next = cardIndex > -1 && cardIndex < buttons.length ? buttons[cardIndex] : null;
      var count = page.querySelector('.heat-wishlist__count');
      var bar = count && count.closest ? count.closest('.heat-wishlist__bar') : null;
      if (bar && bar.hidden) count = null;
      var target = next || count || page.querySelector('.heat-wishlist__empty');
      if (!target || !target.focus) return;
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus();
    });
  }

  /*
    The count line follows the CANONICAL list length, the same number the
    header heart shows, for guests and signed-in shoppers alike. Counting the
    cards on the page would undercount while the async product lookups are
    still in flight.
  */
  function updatePageCount(page, shown) {
    var left = state.length;
    var bar = page.querySelector('.heat-wishlist__bar');
    if (bar) {
      var countEl = bar.querySelector('.heat-wishlist__count');
      if (countEl) {
        var text = left === 1 ? '1 item saved' : left + ' items saved';
        /*
          With a filter on, the canonical count alone reads as a miscount next
          to a shorter grid, so say both numbers. `shown` is only meaningful
          once the cards have been drawn, hence the 0 pass before the lookups.
        */
        if (viewFilter && shown) text = shown + ' of ' + text;
        countEl.textContent = text;
      }
      bar.hidden = left === 0;
    }
    var empty = page.querySelector('[data-heat-wishlist-empty]');
    if (empty) empty.hidden = left !== 0;
  }

  /*
    Every card that can be added carries [data-heat-wishlist-addable] with the
    variant id to add and [data-available], set by the resolver, so a
    substituted row contributes its CURRENT variant. "No longer available"
    cards carry neither and are skipped silently. The state fallback below only
    runs if add-all is clicked before the cards have finished rendering.
  */
  function collectAddAll(page) {
    var rows = page.querySelectorAll('[data-heat-wishlist-addable]');
    if (rows.length) {
      var items = [];
      var skipped = 0;
      var seen = {};
      for (var i = 0; i < rows.length; i++) {
        var vid = rows[i].getAttribute('data-heat-wishlist-addable');
        if (!vid || seen[vid]) continue;
        seen[vid] = true;
        if (rows[i].getAttribute('data-available') === 'true') {
          items.push({ id: vid, quantity: 1 });
        } else {
          skipped++;
        }
      }
      return Promise.resolve({ items: items, skipped: skipped });
    }

    var renderable = visibleRows().filter(function (row) { return !!row.handle; });
    return Promise.all(renderable.map(function (row) {
      return fetchProduct(row.handle).then(function (product) {
        return resolveRow(product, row.vid);
      });
    })).then(function (resolvedRows) {
      var items = [];
      var skipped = 0;
      var seen = {};
      resolvedRows.forEach(function (resolved) {
        if (!resolved) return;
        var id = String(resolved.variant.id);
        if (seen[id]) return;
        seen[id] = true;
        if (resolved.variant.available) items.push({ id: id, quantity: 1 });
        else skipped++;
      });
      return { items: items, skipped: skipped };
    });
  }

  /* ------------------------------------------------- share link (owner) */

  /*
    The owner's own controls. Signed in only: a guest has no list on the server
    to share, so sections/heat-wishlist.liquid renders a one-line nudge in the
    button's place instead and nothing here binds.

    The panel is built once by Liquid and only ever shown, hidden and filled in
    from here, so there is no markup to keep in two places.
  */
  function shareStatus(panel, message) {
    var node = panel.querySelector('[data-heat-wishlist-share-status]');
    if (!node) return;
    node.textContent = message;
    node.hidden = !message;
  }

  /*
    The share URL the owner is shown, and which of their lists it covers.
    Every URL comes from the server exactly as it is shown: the whole-wishlist
    one from the panel's /share response, and each list's own from
    /share?list=<label>. A list link carries a token bound to that label, so
    nothing here may build one by appending &list= to the whole-wishlist URL:
    that would hand a gift giver the whole wishlist, since they could just
    delete the suffix.

    List URLs are fetched on first pick and cached per lowercased label. The
    cache is dropped whenever the panel gets a fresh enable, disable or rotate
    answer (applyShare), because a rotate changes the salt and every cached
    link with it. shareGeneration ticks at the same moment, so a list fetch
    that was already in flight cannot land a pre-rotate link afterwards.
  */
  var shareBaseUrl = '';
  var shareListChoice = '';
  var shareListUrls = {};
  var shareGeneration = 0;
  var SHARE_LOAD_ERROR = 'Could not load your share link just now. Please try again.';

  function applyShare(panel, res) {
    panel.hidden = false;
    var ok = !!(res && res.ok);
    var on = !!(ok && res.enabled && res.url);

    shareGeneration++;
    shareListUrls = {};

    var row = panel.querySelector('[data-heat-wishlist-share-row]');
    var note = panel.querySelector('[data-heat-wishlist-share-note]');
    var links = panel.querySelector('[data-heat-wishlist-share-links]');

    if (row) row.hidden = !on;
    if (note) note.hidden = !on;
    if (links) links.hidden = !on;
    shareBaseUrl = on ? String(res.url) : '';
    renderSharePick(panel, on);
    if (on) writeShareUrl(panel);

    if (!ok) shareStatus(panel, SHARE_LOAD_ERROR);
    else if (!on) shareStatus(panel, 'Sharing is off. Use Share my wishlist to make a new link.');
    else shareStatus(panel, '');
  }

  /*
    One option per label in use, plus Whole wishlist. Hidden outright when the
    owner has no lists, so sharing stays a single button for anybody who never
    made one.
  */
  function renderSharePick(panel, on) {
    var pick = panel.querySelector('[data-heat-wishlist-share-pick]');
    var select = panel.querySelector('[data-heat-wishlist-share-list]');
    if (!pick || !select) return;

    var labels = labelsInUse();
    if (shareListChoice && !hasLabel(labels, shareListChoice)) shareListChoice = '';
    pick.hidden = !on || labels.length === 0;

    select.innerHTML = '';
    select.appendChild(listOption('', 'Whole wishlist'));
    for (var i = 0; i < labels.length; i++) {
      select.appendChild(listOption(labels[i], labels[i]));
      if (sameLabel(labels[i], shareListChoice)) shareListChoice = labels[i];
    }
    select.value = shareListChoice;
  }

  /*
    Shows the link for the current choice. A list link not fetched yet leaves
    the box empty and Copy disabled until it arrives, so the owner can never
    copy the previous choice's link by mistake. An answer for a choice the
    owner has since moved off, or from before a rotate, is dropped.
  */
  function writeShareUrl(panel) {
    if (!shareListChoice) {
      setShareUrl(panel, shareBaseUrl);
      return;
    }
    var label = shareListChoice;
    var key = label.toLowerCase();
    if (shareListUrls[key]) {
      setShareUrl(panel, shareListUrls[key]);
      return;
    }
    setShareUrl(panel, '');
    var generation = shareGeneration;
    proxyGet('/share?list=' + encodeURIComponent(label)).then(function (res) {
      if (generation !== shareGeneration || !sameLabel(label, shareListChoice)) return;
      if (res && res.ok && !res.enabled) {
        // Switched off from another tab or device since the panel opened.
        applyShare(panel, res);
        return;
      }
      /*
        The answer must name the list it is for. A server without list links
        ignores ?list= and returns the WHOLE-wishlist URL, which must never be
        shown as a list's link, so anything else counts as a failure.
      */
      if (!res || !res.ok || !res.url || !sameLabel(res.list, label)) {
        shareStatus(panel, SHARE_LOAD_ERROR);
        return;
      }
      shareListUrls[key] = String(res.url);
      setShareUrl(panel, shareListUrls[key]);
    });
  }

  function setShareUrl(panel, url) {
    var input = panel.querySelector('[data-heat-wishlist-share-url]');
    var copy = panel.querySelector('[data-heat-wishlist-share-copy]');
    if (input) input.value = url || '';
    if (copy) copy.disabled = !url;
  }

  function shareAction(action) {
    return proxy('/share', { action: action });
  }

  function copyShareLink(panel) {
    var input = panel.querySelector('[data-heat-wishlist-share-url]');
    if (!input || !input.value) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(input.value).then(function () {
        shareStatus(panel, 'Link copied.');
      }, function () {
        legacyCopy(panel, input);
      });
      return;
    }
    legacyCopy(panel, input);
  }

  /* Older browsers, and any context where the async clipboard is blocked. */
  function legacyCopy(panel, input) {
    try {
      input.focus();
      input.select();
      if (input.setSelectionRange) input.setSelectionRange(0, input.value.length);
      var copied = document.execCommand('copy');
      shareStatus(panel, copied ? 'Link copied.' : 'Copy the link with your keyboard.');
    } catch (e) {
      shareStatus(panel, 'Copy the link with your keyboard.');
    }
  }

  function bindShare() {
    if (!customerId) return;
    var page = document.querySelector('.heat-wishlist');
    if (!page) return;
    var panel = page.querySelector('[data-heat-wishlist-share-panel]');
    var trigger = page.querySelector('[data-heat-wishlist-share]');
    if (!panel || !trigger) return;

    trigger.addEventListener('click', function (event) {
      event.preventDefault();
      trigger.disabled = true;
      proxyGet('/share').then(function (res) {
        // Never shared before, or switched off since: turn it back on.
        if (res && res.ok && !res.enabled) return shareAction('enable');
        return res;
      }).then(function (res) {
        trigger.disabled = false;
        applyShare(panel, res);
      });
    });

    panel.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      /*
        Close only hides the panel. Nothing is turned off and nothing is
        forgotten, so Share my wishlist reopens it in the same state.
      */
      if (target.closest('[data-heat-wishlist-share-close]')) {
        event.preventDefault();
        panel.hidden = true;
        trigger.focus();
        return;
      }

      if (target.closest('[data-heat-wishlist-share-copy]')) {
        event.preventDefault();
        copyShareLink(panel);
        return;
      }

      var action = '';
      if (target.closest('[data-heat-wishlist-share-stop]')) action = 'disable';
      else if (target.closest('[data-heat-wishlist-share-rotate]')) action = 'rotate';
      if (!action) return;

      event.preventDefault();
      shareStatus(panel, action === 'disable' ? 'Turning sharing off...' : 'Making a new link...');
      shareAction(action).then(function (res) {
        applyShare(panel, res);
        if (res && res.ok && action === 'rotate' && res.url) {
          shareStatus(panel, 'New link ready. Your old links no longer work, including links to single lists.');
        }
      });
    });

    panel.addEventListener('change', function (event) {
      var select = event.target.closest
        ? event.target.closest('[data-heat-wishlist-share-list]')
        : null;
      if (!select) return;
      shareListChoice = select.value || '';
      shareStatus(panel, '');
      writeShareUrl(panel);
    });
  }

  /* -------------------------------------------- shared view (gift giver) */

  var sharedOwner = '';

  /*
    Everything the owner sees is hidden before the first fetch resolves: this
    page belongs to somebody else, and a gift giver must not see (or be able to
    click) a control that would write to their own list.
  */
  function prepareSharedPage(page) {
    page.classList.add('heat-wishlist--shared');
    var owned = page.querySelectorAll(
      '.heat-wishlist__bar, .heat-wishlist__nudge, [data-heat-wishlist-tools],'
      + ' [data-heat-wishlist-empty], [data-heat-wishlist-share-panel]'
    );
    for (var i = 0; i < owned.length; i++) owned[i].hidden = true;
    var list = page.querySelector('[data-heat-wishlist-list]');
    if (list) list.innerHTML = '';
  }

  function loadShared() {
    var page = document.querySelector('.heat-wishlist');
    if (!page) return;
    prepareSharedPage(page);
    var query = '/shared?t=' + encodeURIComponent(shareToken);
    if (sharedList) query += '&list=' + encodeURIComponent(sharedList);
    proxyGet(query).then(function (res) {
      applyShared(page, res);
    });
  }

  function applyShared(page, res) {
    if (!page) return;
    var head = page.querySelector('[data-heat-wishlist-shared-head]');
    var dead = page.querySelector('[data-heat-wishlist-shared-dead]');
    var list = page.querySelector('[data-heat-wishlist-list]');

    if (!res || res.ok !== true) {
      // Invalid, rotated or switched off. One clear line, and a way onward.
      if (head) head.hidden = true;
      if (list) list.innerHTML = '';
      if (dead) dead.hidden = false;
      return;
    }

    if (dead) dead.hidden = true;
    sharedOwner = res.owner ? String(res.owner) : '';
    /*
      The server decides the label: it echoes back the owner's own casing for
      the list it filtered on, so the heading matches what the owner typed.
    */
    var label = res.list ? String(res.list) : '';
    if (head) {
      head.hidden = false;
      var title = head.querySelector('[data-heat-wishlist-shared-title]');
      if (title) {
        var owner = sharedOwner ? sharedOwner + "'s " : "A friend's ";
        title.textContent = owner + (label ? label + ' list' : 'wishlist');
      }
    }
    renderSharedCards(page, Array.isArray(res.items) ? res.items : [], label);
  }

  function normalizeSharedItem(raw) {
    var purchased = null;
    if (raw && raw.purchased) {
      purchased = { at: raw.purchased.at == null ? '' : String(raw.purchased.at) };
    }
    return {
      vid: String(raw && raw.vid != null ? raw.vid : ''),
      pid: String(raw && raw.pid != null ? raw.pid : ''),
      handle: String(raw && raw.handle != null ? raw.handle : ''),
      at: String(raw && raw.at != null ? raw.at : ''),
      list: cleanLabel(raw && raw.list),
      purchased: purchased
    };
  }

  function renderSharedCards(page, items, label) {
    var list = page.querySelector('[data-heat-wishlist-list]');
    if (!list) return;

    pageTicket++;
    var ticket = pageTicket;

    Promise.all(items.map(function (raw) {
      var row = normalizeSharedItem(raw);
      if (!row.handle) return Promise.resolve({ row: row, product: null });
      return fetchProduct(row.handle).then(function (product) {
        return { row: row, product: product };
      });
    })).then(function (results) {
      if (ticket !== pageTicket) return;
      list.innerHTML = '';
      var drawn = 0;
      results.forEach(function (result) {
        var resolved = resolveRow(result.product, result.row.vid);
        // A row nobody can buy is no use to a gift giver, so it is left out.
        if (!resolved) return;
        list.appendChild(sharedCard(result.row, result.product, resolved));
        drawn++;
      });
      if (!drawn) {
        var none = document.createElement('p');
        none.className = 'heat-wishlist__none';
        none.textContent = label
          ? 'Nothing on this list yet. '
          : 'Nothing on this wishlist right now. ';
        var browse = document.createElement('a');
        browse.href = '/collections/hot-sauce';
        browse.textContent = 'Browse hot sauces';
        none.appendChild(browse);
        none.appendChild(document.createTextNode('.'));
        list.appendChild(none);
      }
    });
  }

  function sharedCard(row, product, resolved) {
    var variant = resolved.variant;

    var card = document.createElement('div');
    card.className = 'heat-wishlist__card' + (row.purchased ? ' is-purchased' : '');
    card.setAttribute('data-heat-wishlist-card', row.vid);

    var link = document.createElement('a');
    link.className = 'heat-wishlist__card-link';
    link.href = '/products/' + product.handle + '?variant=' + variant.id;

    if (product.featured_image) link.appendChild(cardImage(product));

    var title = document.createElement('span');
    title.className = 'heat-wishlist__card-title';
    title.textContent = product.title;
    link.appendChild(title);
    card.appendChild(link);

    var brand = brandLine(product);
    if (brand) card.appendChild(brand);

    if (variant.title && variant.title !== 'Default Title') {
      var vt = document.createElement('span');
      vt.className = 'heat-wishlist__card-variant';
      vt.textContent = variant.title;
      card.appendChild(vt);
    }

    var price = document.createElement('span');
    price.className = 'heat-wishlist__card-price';
    price.textContent = money(variant.price);
    card.appendChild(price);

    if (row.list) card.appendChild(listChip(row.list));

    if (row.purchased) {
      var bought = document.createElement('span');
      bought.className = 'heat-wishlist__card-chip heat-wishlist__card-chip--bought';
      bought.textContent = 'Already bought';
      card.appendChild(bought);
    }

    var actions = document.createElement('div');
    actions.className = 'heat-wishlist__card-actions';

    var atc = document.createElement('button');
    atc.type = 'button';
    atc.className = 'heat-wishlist__btn heat-wishlist__btn--primary';
    atc.textContent = variant.available ? 'Add to cart' : 'Sold out';
    atc.disabled = !variant.available;
    if (variant.available) {
      atc.addEventListener('click', function () {
        var line = { id: variant.id, quantity: 1 };
        /*
          Underscore prefix: Shopify hides _-prefixed line properties from the
          cart, the emails and the packing slip, but the order still carries
          them in admin, which is where "who is this for" is worth knowing.
        */
        if (sharedOwner) line.properties = { _heat_gift_for: sharedOwner };
        addToCart([line], atc, 'Add to cart');
      });
    }
    actions.appendChild(atc);

    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'heat-wishlist__btn heat-wishlist__btn--ghost';
    toggle.textContent = row.purchased ? 'Undo' : 'Bought this';
    toggle.addEventListener('click', function () {
      markPurchased(row.vid, !row.purchased, toggle);
    });
    actions.appendChild(toggle);

    card.appendChild(actions);
    return card;
  }

  /*
    No name is asked for and none is stored. A gift giver marking a bottle is
    one click: the owner only ever sees "Already bought" anyway, and the giver
    should not have to type their name to spare the owner a duplicate.

    `list` rides along so the payload that comes back stays narrowed to the
    same sublist the giver is looking at, and because a list link's token only
    verifies together with its label.
  */
  function markPurchased(vid, purchased, button) {
    button.disabled = true;
    var payload = { t: shareToken, vid: vid, purchased: !!purchased };
    if (sharedList) payload.list = sharedList;
    proxy('/shared/purchased', payload).then(function (res) {
      button.disabled = false;
      /*
        Any refusal gets the same line, including not_on_list (the owner moved
        or removed that bottle after this page loaded): the giver can reload,
        and nothing about the rest of the wishlist is worth saying here.
      */
      if (!res || res.ok !== true) {
        say('Could not update that just now. Please try again.');
        return;
      }
      say('');
      applyShared(document.querySelector('.heat-wishlist'), res);
    });
  }

  /* ------------------------------------------------------------ render */

  function render() {
    refreshButtons();
    refreshCounts();
    renderPage();
  }

  /* -------------------------------------------------------------- init */

  function init() {
    /*
      Guest: import the legacy list on every load. No flag, no expiry, so the
      old list stays recoverable for as long as the key is in the browser.

      Signed in: the same import feeds the merge below, but exactly once (see
      LEGACY_MERGED_KEY), because the account list is not the key the import
      dedupes against.
    */
    if (!customerId) {
      state = normalizeItems(importLegacy(readGuest()));
      writeGuest(state);
    } else if (!legacyMergedIntoAccount()) {
      var guest = importLegacy(readGuest());
      if (guest.length) writeGuest(normalizeItems(guest));
    }

    observeSelections();
    observeBody();
    bindButtons();

    /*
      Shared mode binds NONE of the owner controls and runs no merge. The only
      wishlist state it touches is the visitor's own header count, which
      render() below keeps accurate exactly as it would on any other page.
    */
    if (sharedMode) {
      loadShared();
    } else {
      bindPageControls();
      bindShare();
    }

    render();

    if (customerId && !sharedMode) mergeGuestIntoAccount();

    // Option inputs fire change before Empire settles the variant attribute.
    document.addEventListener('change', function () {
      setTimeout(function () { observeSelections(); refreshButtons(); }, 0);
      setTimeout(refreshButtons, 250);
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
